import { contrast, parseColor } from './contrast';

/** Tokens do Lume que a marca pode definir (U16, discovery/07 §7.3). O resto é o produto. */
export interface AccentTokens {
  readonly accent: string;
  readonly accentHover: string;
  readonly accentPress: string;
  readonly accentText: string;
  readonly accentWash: string;
  readonly accentLine: string;
  readonly fgOnAccent: string;
  readonly borderFocus: string;
}

interface Oklch {
  readonly l: number;
  readonly c: number;
  readonly h: number;
}

const MIN_TEXT = 4.5;
const LIGHT = { base: '#fafafa', raise: '#ffffff', onAccent: '#ffffff', wash: 0.09, line: 0.28 } as const;
const DARK = { base: '#171717', raise: '#262626', onAccent: '#171717', wash: 0.14, line: 0.32 } as const;

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function hexToOklch(hex: string): Oklch {
  const [r8, g8, b8] = parseColor(hex);
  const [r, g, b] = [r8 / 255, g8 / 255, b8 / 255].map(toLinear) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { l: L, c: Math.hypot(A, B), h: Math.atan2(B, A) };
}

function oklchToRgb({ l: L, c, h }: Oklch): [number, number, number] {
  const A = c * Math.cos(h);
  const B = c * Math.sin(h);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const inGamut = (rgb: readonly number[]) => rgb.every((v) => v >= -1e-4 && v <= 1 + 1e-4);

/** Converte para hex; fora do sRGB, reduz o croma (matiz fixa) por busca binária. */
function toHex(color: Oklch): string {
  let target = color;
  if (!inGamut(oklchToRgb(target))) {
    let lo = 0;
    let hi = color.c;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(oklchToRgb({ ...color, c: mid }))) lo = mid;
      else hi = mid;
    }
    target = { ...color, c: lo };
  }
  return `#${oklchToRgb(target)
    .map((v) => Math.round(Math.min(1, Math.max(0, toGamma(Math.min(1, Math.max(0, v))))) * 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

const clampL = (l: number) => Math.min(0.99, Math.max(0.01, l));

/** Anda em L (passo 0,005) até o predicado passar; devolve null se nem o extremo passar. */
function walk(start: Oklch, dir: 1 | -1, ok: (hex: string) => boolean): Oklch | null {
  for (let l = start.l; l >= 0 && l <= 1; l += dir * 0.005) {
    const candidate = { ...start, l: clampL(l) };
    if (ok(toHex(candidate))) return candidate;
  }
  return null;
}

function alpha(hex: string, a: number): string {
  const [r, g, b] = parseColor(hex);
  return `rgb(${String(r)} ${String(g)} ${String(b)} / ${String(a)})`;
}

/** Deriva os tokens de acento de um tema a partir de uma semente, com AA medido. */
export function deriveAccent(seed: string, theme: 'light' | 'dark'): AccentTokens | null {
  const t = theme === 'light' ? LIGHT : DARK;
  const dir: 1 | -1 = theme === 'light' ? -1 : 1;
  const base = hexToOklch(seed);
  const button = (hex: string) => contrast(t.onAccent, hex) >= MIN_TEXT;
  const accent = walk(base, dir, button);
  if (!accent) return null;
  const accentHex = toHex(accent);

  // Semente muito escura no claro (ou muito clara no escuro): o hover anda para o outro lado.
  const inverted = theme === 'light' ? accent.l < 0.35 : accent.l > 0.85;
  const shift = (delta: number) => {
    const moved = { ...accent, l: clampL(accent.l + delta) };
    return button(toHex(moved)) ? moved : (walk(moved, dir, button) ?? accent);
  };
  const hover = theme === 'light' ? shift(inverted ? 0.07 : -0.07) : shift(inverted ? -0.08 : 0.08);
  const press = theme === 'light' ? shift(inverted ? 0.13 : -0.13) : shift(inverted ? -0.14 : -0.06);

  const wash = alpha(accentHex, t.wash);
  const readable = (hex: string) => contrast(hex, t.base) >= MIN_TEXT && contrast(hex, t.raise) >= MIN_TEXT && contrast(hex, wash, t.raise) >= MIN_TEXT;
  const text = walk(accent, dir, readable);
  if (!text) return null;

  return {
    accent: accentHex,
    accentHover: toHex(hover),
    accentPress: toHex(press),
    accentText: toHex(text),
    accentWash: wash,
    accentLine: alpha(accentHex, t.line),
    fgOnAccent: t.onAccent,
    borderFocus: accentHex,
  };
}
